import { FormEvent, useState } from "react";

import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { subjectJosa } from "@buildpy/core/lib/josa";
import { ShareVerifyResult } from "@buildpy/core/modules/share/action-types";
import { verifyShare } from "@/api/actions/share";

interface Props {
  token: string;
  orgName: string;
  // 계약서 링크가 같은 폼을 쓴다 — 비우면 견적서 문구·액션이다.
  docName?: string;
  verify?: (token: string, last4: string) => Promise<ShareVerifyResult>;
}

/**
 * 뒷 4자리 확인 폼. 고객이 보는 첫 화면이라 설명 한 줄과 입력칸 하나만 둔다.
 * 성공하면 서버가 쿠키를 심고 응답 봉투의 invalidate 로 화면이 다시 읽혀 견적서가 나온다.
 */
const ShareGate = ({ token, orgName, docName = "견적서", verify = verifyShare }: Props) => {
  const [value, setValue] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [locked, setLocked] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending || locked) return;

    setPending(true);
    setMessage(null);
    try {
      const result = await verify(token, value);
      // 성공 봉투의 invalidate 가 이미 이 화면 읽기를 다시 읽는다 — 여기서 refresh 를 또 부르면 같은 GET 이 두 번 나간다.
      if (result.ok) return;
      setMessage(result.message);
      setLocked(result.locked);
    } catch {
      setMessage("확인하지 못했습니다. 잠시 뒤에 다시 시도해 주세요");
    } finally {
      setPending(false);
    }
  };

  return (
    <SectionCard
      as="form"
      className="mx-auto flex w-full max-w-sm flex-col gap-y-4 p-5"
      padding="none"
      onSubmit={submit}>
      <div className="flex flex-col gap-y-1">
        <h1 className="text-lg font-semibold">{docName} 확인</h1>
        <p className="text-sm text-muted-foreground">
          {orgName}{subjectJosa(orgName)} 보낸 {docName}입니다. 본인 확인을 위해 휴대폰 번호 뒤
          4자리를 입력해 주세요.
        </p>
      </div>

      <Field label="휴대폰 뒤 4자리">
        <Input
          autoFocus
          className="text-center tracking-[0.5em] tabular-nums"
          disabled={locked}
          inputMode="numeric"
          inputSize="lg"
          maxLength={4}
          name="last4"
          placeholder="0000"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 4))}
        />
      </Field>

      {message && (
        <p className="text-sm text-destructive" role="alert">
          {message}
        </p>
      )}

      <Button
        className="w-full"
        disabled={pending || locked || value.length !== 4}
        type="submit"
        variant="primary">
        {pending ? "확인 중…" : `${docName} 보기`}
      </Button>
    </SectionCard>
  );
};

export default ShareGate;
